import type { trpcQueryUtils } from '@client/trpc/client';
import { DETAIL_RECORD_PREFETCH_OPTIONS } from '@client/trpc/query-options';

/**
 * Input shared by the manage tab's mount-time config lookups and the route-level
 * prefetch. The two must serialise to the same query key, or the prefetch warms
 * nothing — import this constant instead of re-typing the literal.
 */
export const INCIDENT_CONFIG_LIST_INPUT = {
    orderBy: { sortOrder: 'asc' },
    where: { isDisabled: false },
} as const;

/**
 * Fire-and-forget warm-up for the incident details page. `getIncident` gates the
 * page's LCP element (the description rich text) and, transitively, every
 * manage-tab query — none of them can start until it resolves and the tab
 * mounts. Firing from `beforeLoad` overlaps the fetch with route-chunk parsing
 * and the auth-bootstrap render gate. Failures fall through to the render-time
 * `useQuery` retry path.
 */
export function prefetchIncidentDetails(trpcUtils: typeof trpcQueryUtils, id: bigint): void {
    void trpcUtils.incident.getIncident.prefetch({ id }, DETAIL_RECORD_PREFETCH_OPTIONS);
    void trpcUtils.incident.getIncidentAiAnalysis.prefetch({ incidentId: id }, DETAIL_RECORD_PREFETCH_OPTIONS);
    void trpcUtils.incident.impactedParty.listIncidentImpactedParties.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.identificationMethod.listIncidentIdentificationMethods.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.remediationOption.listIncidentRemediationOptions.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.solutionImplemented.listIncidentSolutionsImplemented.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.rootCause.listIncidentRootCauses.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
}
